import { useEffect, useState } from 'react';
import {
  SearchableWorkflowRunFilterInput,
  useSearchWorkflowRunsByCandidateProfilesLazyQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { fetchAllPaginatedItems } from 'core/utils/apollo/fetchAllPaginatedItems';

type UseEnrolledWorkflowCandidatesProps = {
  candidateProfileIds: string[];
  workflowId: string | null;
};

type EnrolmentState = {
  enrolledIds: string[];
  notEnrolledIds: string[];
  /**
   * False while the lookup for the current workflow is outstanding. Callers must wait: an
   * unresolved state is indistinguishable from "everyone is already enrolled" by the ids alone.
   */
  isResolved: boolean;
};

const initialState: EnrolmentState = {
  enrolledIds: [],
  notEnrolledIds: [],
  isResolved: true
};

/**
 * Which of the selected candidates already have a run on the chosen workflow, so the
 * drawer can name them and leave them out of the push.
 *
 * Advisory only: workflow-service's conditional-create gate is what actually prevents a
 * double enrolment, so a candidate enrolled between this check and the push is still safe
 * — the count shown just under-reports.
 */
export const useEnrolledWorkflowCandidates = ({
  candidateProfileIds,
  workflowId
}: UseEnrolledWorkflowCandidatesProps) => {
  const [state, setState] = useState<EnrolmentState>(initialState);

  const [searchWorkflowRuns, { loading }] =
    useSearchWorkflowRunsByCandidateProfilesLazyQuery();

  // Keyed on the ids rather than the array, so a caller that rebuilds the array each
  // render doesn't re-run the effect, re-set state and loop.
  const candidateIdsKey = candidateProfileIds.join(',');

  useEffect(() => {
    // Mutable holder rather than a bare flag so the cleanup can cancel this run's own
    // effect without reassigning a binding.
    const run = { isCancelled: false };
    const selectedIds = candidateIdsKey ? candidateIdsKey.split(',') : [];

    if (!workflowId || selectedIds.length === 0) {
      setState({
        enrolledIds: [],
        notEnrolledIds: selectedIds,
        isResolved: true
      });
      return;
    }

    // Nothing is known about this workflow until its lookup lands, so the previous
    // workflow's answer must not stand in for it.
    setState({ enrolledIds: [], notEnrolledIds: [], isResolved: false });

    const filter: SearchableWorkflowRunFilterInput = {
      and: [
        { workflowId: { eq: workflowId } },
        {
          or: selectedIds.map((id) => ({
            candidateProfileId: { eq: id }
          }))
        }
      ]
    };

    const fetchRunsPage = async (nextToken?: string | null) => {
      const { data } = await searchWorkflowRuns({
        variables: {
          filter,
          limit: selectedIds.length,
          nextToken
        }
      });
      return data?.searchWorkflowRuns;
    };

    const fetchEnrolled = async () => {
      try {
        const items = await fetchAllPaginatedItems(fetchRunsPage);

        // A slower request for a workflow the recruiter has since moved off would otherwise
        // land as the current workflow's answer.
        if (run.isCancelled) {
          return;
        }

        const enrolledIds = items.map((item) => item.candidateProfileId);
        const notEnrolledIds = selectedIds.filter(
          (id) => !enrolledIds.includes(id)
        );

        setState({ enrolledIds, notEnrolledIds, isResolved: true });
      } catch {
        if (run.isCancelled) {
          return;
        }

        // Fail open. This lookup is advisory, so pushing everyone and letting the
        // conditional-create gate reject the duplicates costs nothing, while staying
        // unresolved would leave the recruiter unable to push at all.
        setState({
          enrolledIds: [],
          notEnrolledIds: selectedIds,
          isResolved: true
        });
      }
    };

    fetchEnrolled();

    return () => {
      run.isCancelled = true;
    };
  }, [candidateIdsKey, workflowId]);

  return {
    ...state,
    isLoading: loading
  };
};
